import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-policy-page">
      <section className="py-20 md:py-28 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-6">
              <Shield className="text-[#A67A5B]" size={20} />
              <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B]">Your Privacy Matters</p>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#2A2421] mb-4 leading-tight">
              Privacy Policy
            </h1>
            <p className="font-sans text-sm text-[#7A736E] mb-12">Last updated: April 2026</p>

            <div className="space-y-10 font-sans text-[#2A2421]/80 leading-relaxed">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Who We Are</h2>
                <p className="text-base">
                  Led By Faith Studios is a faith-based publishing and creative brand. This website serves as our central hub for books, journals, and projects. Your trust matters deeply to us, and we are committed to protecting your personal information.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Information We Collect</h2>
                <p className="text-base mb-3">We may collect the following information when you interact with our website:</p>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#7A736E]">
                  <li>Name and email address when you submit our contact form or sign up for updates</li>
                  <li>Messages and inquiry details you provide through our contact form</li>
                  <li>Basic usage data such as pages visited and time spent on the site</li>
                </ul>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">How We Use Your Information</h2>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#7A736E]">
                  <li>To respond to your inquiries and messages</li>
                  <li>To send updates about new books, journals, and projects if you have opted in</li>
                  <li>To improve our website and your experience</li>
                  <li>To process any orders or requests you make</li>
                </ul>
                <p className="text-sm text-[#7A736E] mt-3">We will never sell, rent, or share your personal information with third parties for marketing purposes.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Third-Party Services</h2>
                <p className="text-base">
                  Our website may link to third-party platforms such as Amazon and Shopify for purchases. These platforms have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of external sites.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Cookies</h2>
                <p className="text-base">
                  Our website may use basic cookies to improve your browsing experience. These cookies do not collect personally identifiable information. You can adjust your browser settings to decline cookies if you prefer.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Your Rights</h2>
                <p className="text-base">
                  You have the right to request access to, correction of, or deletion of your personal information at any time. Simply contact us and we will be happy to assist you.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Changes to This Policy</h2>
                <p className="text-base">
                  We may update this Privacy Policy from time to time. Any changes will be reflected on this page with an updated date. We encourage you to review this page periodically.
                </p>
              </div>

              <div className="pt-6 border-t border-[#E8E1D9]">
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Contact Us</h2>
                <p className="text-base mb-4">
                  If you have any questions about this Privacy Policy, please reach out.
                </p>
                <Link
                  to="/contact?inquiry=privacy_policy"
                  data-testid="privacy-contact-link"
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#A67A5B] hover:text-[#8C654A] transition-colors"
                >
                  Contact us here &rarr;
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
